import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  fetchSubPricing, fetchMySubscription, startSubCheckout, openStripePortal,
} from "@/lib/api";
import {
  Sparkles, Check, Loader2, ExternalLink, Crown, ShieldCheck, ReceiptText, CreditCard,
} from "lucide-react";
import { toast } from "sonner";
import PageErrorBoundary from "@/components/PageErrorBoundary";

const PRO_FEATURES = [
  "Unlimited children",
  "AI Copilot (unlimited)",
  "Family Story + Yearbook",
  "Memory Vault Ask",
  "PDF exports",
  "Family Legacy timeline",
];

const FREE_FEATURES = [
  "1 child",
  "Gmail sync",
  "Basic dashboard",
];

function BillingInner() {
  const { user } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null); // lookup_key
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    Promise.all([fetchSubPricing(), fetchMySubscription()])
      .then(([p, s]) => { setPricing(p); setSub(s); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const startCheckout = async (lookup_key) => {
    setStarting(lookup_key);
    try {
      const { checkout_url } = await startSubCheckout(lookup_key);
      window.location.assign(checkout_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setStarting(null);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { portal_url } = await openStripePortal();
      window.location.assign(portal_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open the customer portal.");
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fcc-card p-16 text-center text-[var(--fcc-muted)]">
        <Loader2 className="w-5 h-5 mx-auto animate-spin" />
        <p className="text-sm mt-3">Loading billing…</p>
      </div>
    );
  }

  const isPro = !!(sub?.is_pro || user?.is_pro);
  const proProduct = pricing?.products?.[0];
  const monthly = proProduct?.prices?.find((p) => p.lookup_key === "pro_monthly");
  const yearly = proProduct?.prices?.find((p) => p.lookup_key === "pro_yearly");

  return (
    <div data-testid="billing-page" className="space-y-6">
      <header className="fcc-card-accent p-7 sm:p-9">
        <p className="label-overline flex items-center gap-2"><CreditCard className="w-3 h-3" /> Settings · Billing</p>
        <h1 className="font-display text-3xl text-[var(--fcc-ink)] mt-2">
          {isPro ? "You're on KidHQ Pro" : "Upgrade to KidHQ Pro"}
        </h1>
        <p className="text-sm text-[var(--fcc-ink-soft)] mt-2 max-w-2xl">
          {isPro
            ? "Manage your subscription, update payment method, or download invoices via the Customer Portal."
            : "Unlock the full Family Operating System. Cancel anytime, no lock-in."}
        </p>
      </header>

      {isPro ? (
        <ProStatusCard sub={sub} onPortal={openPortal} portalLoading={portalLoading} />
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="billing-plans">
          <PlanCard
            testid="plan-free"
            title="Free"
            price="$0"
            interval=""
            tagline="Perfect for getting started."
            features={FREE_FEATURES}
            cta="Current plan"
            disabled
          />
          <PlanCard
            testid="plan-pro"
            title="Pro"
            price={monthly ? monthly.display.split("/")[0] : "$9.99"}
            interval={monthly ? `/${monthly.interval}` : "/month"}
            secondaryPrice={yearly ? `or ${yearly.display} (save 34%)` : ""}
            tagline="Everything KidHQ can do."
            features={PRO_FEATURES}
            highlight
            cta="Start monthly · $9.99"
            secondaryCta="Yearly · $79 (save 34%)"
            onClick={() => startCheckout("pro_monthly")}
            onSecondaryClick={() => startCheckout("pro_yearly")}
            loading={starting}
          />
        </div>
      )}

      <TrustRow />

      <Link
        to="/app"
        data-testid="billing-back"
        className="inline-block text-sm text-[var(--fcc-ink-soft)] hover:text-[var(--fcc-green)]"
      >
        ← Back to dashboard
      </Link>
    </div>
  );
}

function PlanCard({
  testid, title, price, interval, secondaryPrice, tagline, features,
  cta, secondaryCta, onClick, onSecondaryClick, disabled, highlight, loading,
}) {
  const isMonthlyLoading = loading === "pro_monthly";
  const isYearlyLoading  = loading === "pro_yearly";
  return (
    <article
      data-testid={testid}
      className={`fcc-card p-6 flex flex-col ${highlight ? "border-2 border-[var(--fcc-green)]" : ""}`}
    >
      {highlight && (
        <span className="fcc-pill text-[10px] uppercase tracking-widest bg-[var(--fcc-green)] text-white self-start mb-3">
          <Crown className="w-3 h-3 inline-block mr-1" /> Most flexible
        </span>
      )}
      <h2 className="font-display text-2xl text-[var(--fcc-ink)]">{title}</h2>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="font-display text-4xl text-[var(--fcc-ink)]">{price}</span>
        {interval && <span className="text-sm text-[var(--fcc-muted)]">{interval}</span>}
      </div>
      {secondaryPrice && <p className="text-xs text-[var(--fcc-green)] font-semibold mt-1">{secondaryPrice}</p>}
      <p className="text-sm text-[var(--fcc-ink-soft)] mt-3">{tagline}</p>
      <ul className="mt-5 space-y-2 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <Check className="w-4 h-4 mt-0.5 text-[var(--fcc-green)] shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className="mt-6 space-y-2">
        <button
          data-testid={`${testid}-primary-cta`}
          onClick={onClick}
          disabled={disabled || isMonthlyLoading || isYearlyLoading}
          className={`w-full text-sm font-semibold py-2.5 rounded-lg inline-flex items-center justify-center gap-2 transition ${
            disabled
              ? "bg-[var(--fcc-cream-2)] text-[var(--fcc-muted)] cursor-default"
              : "bg-[var(--fcc-green)] text-white hover:bg-[var(--fcc-green-dark)]"
          }`}
        >
          {isMonthlyLoading && <Loader2 className="w-4 h-4 animate-spin" />}
          {cta}
        </button>
        {secondaryCta && (
          <button
            data-testid={`${testid}-secondary-cta`}
            onClick={onSecondaryClick}
            disabled={isYearlyLoading || isMonthlyLoading}
            className="w-full text-sm font-semibold py-2.5 rounded-lg border border-[var(--fcc-border)] bg-white text-[var(--fcc-ink)] hover:bg-[var(--fcc-cream-2)] inline-flex items-center justify-center gap-2"
          >
            {isYearlyLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            {secondaryCta}
          </button>
        )}
      </div>
    </article>
  );
}

function ProStatusCard({ sub, onPortal, portalLoading }) {
  const s = sub?.subscription || {};
  const renewsAt = s.current_period_end
    ? new Date(s.current_period_end * 1000).toLocaleDateString()
    : null;
  const canceling = !!s.cancel_at_period_end;
  const status = s.status || "active";
  const interval = s.interval || "month";
  return (
    <article data-testid="pro-status-card" className="fcc-card p-6 space-y-4">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <p className="label-overline flex items-center gap-2"><Crown className="w-3 h-3 text-[var(--fcc-green)]" /> Active plan</p>
          <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-1">KidHQ Pro · {interval === "year" ? "Yearly" : "Monthly"}</h2>
        </div>
        <span className={`fcc-pill text-xs ${status === "active" || status === "trialing" ? "bg-[var(--fcc-green)] text-white" : "bg-amber-100 text-amber-800"}`}>
          {status}
        </span>
      </div>
      <ul className="text-sm text-[var(--fcc-ink-soft)] space-y-1">
        {renewsAt && !canceling && <li>Renews on <strong className="text-[var(--fcc-ink)]">{renewsAt}</strong>.</li>}
        {renewsAt && canceling && <li>Cancels at period end · <strong className="text-[var(--fcc-ink)]">{renewsAt}</strong>.</li>}
        {!renewsAt && <li>Subscription is active. Manage everything from the Stripe Customer Portal.</li>}
      </ul>
      <button
        data-testid="open-customer-portal"
        onClick={onPortal}
        disabled={portalLoading}
        className="fcc-btn-primary text-sm py-2.5 px-4 inline-flex items-center gap-2"
      >
        {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
        Manage in Customer Portal
      </button>
      <p className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]">
        Update card, download invoices, or cancel any time.
      </p>
    </article>
  );
}

function TrustRow() {
  return (
    <ul className="grid sm:grid-cols-3 gap-3">
      <li className="fcc-card p-4 flex items-start gap-3">
        <ShieldCheck className="w-4 h-4 mt-0.5 text-[var(--fcc-green)]" />
        <div>
          <p className="text-sm font-semibold text-[var(--fcc-ink)]">PCI-safe checkout</p>
          <p className="text-xs text-[var(--fcc-muted)]">Payment happens on Stripe. We never see your card.</p>
        </div>
      </li>
      <li className="fcc-card p-4 flex items-start gap-3">
        <ReceiptText className="w-4 h-4 mt-0.5 text-[var(--fcc-green)]" />
        <div>
          <p className="text-sm font-semibold text-[var(--fcc-ink)]">Full invoices + receipts</p>
          <p className="text-xs text-[var(--fcc-muted)]">Delivered automatically. Downloadable from the portal.</p>
        </div>
      </li>
      <li className="fcc-card p-4 flex items-start gap-3">
        <Sparkles className="w-4 h-4 mt-0.5 text-[var(--fcc-green)]" />
        <div>
          <p className="text-sm font-semibold text-[var(--fcc-ink)]">Cancel anytime</p>
          <p className="text-xs text-[var(--fcc-muted)]">Zero commitment, keep Pro until the end of the period.</p>
        </div>
      </li>
    </ul>
  );
}

export default function BillingPage() {
  return (
    <PageErrorBoundary scope="billing">
      <BillingInner />
    </PageErrorBoundary>
  );
}
